import React from 'react';
import Icon from '../../../components/AppIcon';

const BookingSummary = ({ selectedPermit, selectedDate, groupSize = 1 }) => {
  const calculateTotal = () => {
    if (!selectedPermit) return { php: 0, usd: 0 };
    
    const phpPrice = parseInt(selectedPermit?.price?.php?.replace(/[₱,]/g, ''));
    const usdPrice = parseInt(selectedPermit?.price?.usd?.replace(/[$,]/g, ''));
    
    return {
      php: phpPrice * groupSize,
      usd: usdPrice * groupSize
    };
  };

  const total = calculateTotal();

  const formatDate = (date) => {
    if (!date) return 'Not selected';
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (!selectedPermit) {
    return (
      <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
        <div className="text-center py-8">
          <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-[var(--color-muted-foreground)]">Select a permit type to see booking summary</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)] sticky top-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-lg bg-[var(--color-accent)]">
          <Icon name="Receipt" size={24} color="var(--color-accent-foreground)" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-[var(--color-foreground)]">Booking Summary</h3>
          <p className="text-sm text-[var(--color-muted-foreground)]">Review your selection</p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="pb-4 border-b border-[var(--color-border)]">
          <div className="flex items-start justify-between mb-2">
            <span className="text-sm text-[var(--color-muted-foreground)]">Permit Type</span>
            <span className="text-sm font-medium text-[var(--color-foreground)] text-right">{selectedPermit?.name}</span>
          </div>
          <div className="flex items-start justify-between mb-2">
            <span className="text-sm text-[var(--color-muted-foreground)]">Duration</span>
            <span className="text-sm font-medium text-[var(--color-foreground)]">{selectedPermit?.duration}</span>
          </div>
          <div className="flex items-start justify-between">
            <span className="text-sm text-[var(--color-muted-foreground)]">Visit Date</span>
            <span className="text-sm font-medium text-[var(--color-foreground)] text-right max-w-[200px]">
              {formatDate(selectedDate)}
            </span>
          </div>
        </div>

        <div className="pb-4 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-[var(--color-muted-foreground)]">Group Size</span>
            <span className="text-sm font-medium text-[var(--color-foreground)]">{groupSize} {groupSize === 1 ? 'person' : 'people'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--color-muted-foreground)]">Price per person</span>
            <div className="text-right">
              <div className="text-sm font-medium text-[var(--color-foreground)]">{selectedPermit?.price?.php} PHP</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">{selectedPermit?.price?.usd} USD</div>
            </div>
          </div>
        </div>

        <div className="pb-4 border-b border-[var(--color-border)]">
          <h4 className="text-sm font-medium text-[var(--color-foreground)] mb-2">Included Features</h4>
          <div className="space-y-2">
            {selectedPermit?.features?.map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                <Icon name="Check" size={14} color="var(--color-success)" className="mt-0.5" />
                <span className="text-xs text-[var(--color-muted-foreground)]">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold text-[var(--color-foreground)]">Total Amount</span>
            <div className="text-right">
              <div className="text-2xl font-bold text-[var(--color-accent)]">
                ₱{total?.php?.toLocaleString()}
              </div>
              <div className="text-sm text-[var(--color-muted-foreground)]">
                ${total?.usd?.toLocaleString()} USD
              </div>
            </div>
          </div>

          <div className="space-y-2 text-xs text-[var(--color-muted-foreground)]">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={12} className="mt-0.5" />
              <span>All fees support conservation efforts and local community development</span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="Shield" size={12} className="mt-0.5" />
              <span>Secure payment processing with SSL encryption</span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="RefreshCw" size={12} className="mt-0.5" />
              <span>Free cancellation up to 48 hours before visit date</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-[var(--color-success)] bg-opacity-10 border border-[var(--color-success)] rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Award" size={16} color="var(--color-success)" className="mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[var(--color-success)] mb-1">UNESCO World Heritage Site</p>
            <p className="text-xs text-[var(--color-foreground)]">
              Your visit supports the protection of irreplaceable biodiversity
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;